import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { DataGrid, GridCellParams, GridColDef } from '@material-ui/data-grid'
import RestoreIcon from '@material-ui/icons/Restore'
import UnarchiveIcon from '@material-ui/icons/Unarchive'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import LayersClearIcon from '@material-ui/icons/LayersClear'
import { Link as RouterLink } from 'react-router-dom'
import { format } from 'date-fns'
import { nl as nlLocale } from 'date-fns/locale'

import { client } from '../../../client'
import { AdminPage } from '../../../layout/AdminPage'
import { AlertDialog } from '../../../components/AlertDialog'
import type { DossierCleanupRow } from '../../../client/client'

type StateFilter = 'marked' | 'soft_deleted'
type ReasonFilter = 'all' | 'patient_card_60d' | 'file_90d'

type SummaryData = {
  markedPatientCards: number
  markedFiles: number
  softDeletedTotal: number
}

const REASON_LABELS: Record<DossierCleanupRow['markedForDeletionReason'], string> = {
  patient_card_60d: 'Patiëntkaart (60 d)',
  file_90d: 'Dossier (90 d)'
}

const formatDate = (value: string | null): string => {
  if (!value) return '—'
  try {
    return format(new Date(value), 'dd-MM-yyyy HH:mm', { locale: nlLocale })
  } catch {
    return '—'
  }
}

const formatPatient = (row: DossierCleanupRow): string => {
  const parts = [row.patientFirstName, row.patientInfix, row.patientLastName]
    .filter(Boolean)
    .map((p) => (p as string).trim())
  return parts.length ? parts.join(' ') : `Patient #${row.patientId}`
}

/**
 * Mirrors the caseload's behavior:
 * - Files (funnels with a TreatmentProgram) open the full record page.
 * - Patient cards (funnels without a TreatmentProgram, i.e. still on the
 *   wachtlijst) open the waitlist patient-details page.
 */
const getRecordPath = (row: DossierCleanupRow): string =>
  row.treatmentProgramId
    ? `/clienten/${row.patientId}/trajecten/${row.treatmentProgramId}`
    : `/wachtlijst/${row.funnelId}`

const DossierCleanupContent = () => {
  const [stateFilter, setStateFilter] = useState<StateFilter>('marked')
  const [reasonFilter, setReasonFilter] = useState<ReasonFilter>('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0) // DataGrid uses 0-based pages
  const [pageSize, setPageSize] = useState(25)

  const [rows, setRows] = useState<DossierCleanupRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [actionInFlight, setActionInFlight] = useState<number | null>(null)
  const [runJobInFlight, setRunJobInFlight] = useState(false)
  const [resetInFlight, setResetInFlight] = useState(false)
  const [bulkInFlight, setBulkInFlight] = useState(false)
  const [selectingAll, setSelectingAll] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  // True once the user has explicitly extended the selection to every row
  // matching the current filter (not just rows on the visible page).
  const [selectionSpansAllPages, setSelectionSpansAllPages] = useState(false)
  // @material-ui/data-grid v4 echoes controlled `selectionModel` prop changes
  // back through `onSelectionModelChange`. Without this guard, programmatic
  // updates from "Select all matching" or "Selectie wissen" trigger our
  // user-action handler, which would then clear the spans flag and wipe the
  // selection. We flip this on before any programmatic update and ignore the
  // next emit from the grid.
  const ignoreNextSelectionEvent = useRef(false)
  const [lastJobMessage, setLastJobMessage] = useState<string | null>(null)

  // Debounce the search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(0)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const fetchSummary = useCallback(async () => {
    try {
      const data = await client.getDossierCleanupSummary()
      setSummary(data)
    } catch (e) {
      console.error('Failed to load dossier cleanup summary', e)
    }
  }, [])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await client.listDossierCleanup({
        state: stateFilter,
        reason: reasonFilter === 'all' ? undefined : reasonFilter,
        search: debouncedSearch || undefined,
        page: page + 1,
        pageSize
      })
      setRows(result.data)
      setTotal(result.total)
    } catch (e: any) {
      console.error('Failed to load dossier cleanup rows', e)
      setError(
        e?.response?.data?.message ||
          e?.message ||
          'Er ging iets mis bij het ophalen van de gegevens.'
      )
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [stateFilter, reasonFilter, debouncedSearch, page, pageSize])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  // Each "clear selection when X changes" effect needs its own first-run
  // guard, otherwise on mount it would pre-arm `ignoreNextSelectionEvent`
  // and swallow the user's very first checkbox click.
  const filtersFirstRun = useRef(true)
  const pageFirstRun = useRef(true)

  // Drop the selection whenever the underlying query changes so we never act
  // on rows that are no longer visible.
  useEffect(() => {
    if (filtersFirstRun.current) {
      filtersFirstRun.current = false
      return
    }
    ignoreNextSelectionEvent.current = true
    setSelectedIds([])
    setSelectionSpansAllPages(false)
  }, [stateFilter, reasonFilter, debouncedSearch])

  // Page changes: keep the selection alive iff the user has opted into a
  // cross-page selection. We deliberately do NOT depend on `selectionSpansAllPages`
  // here, otherwise the effect would fire the moment we flip the flag to true
  // and immediately wipe the just-set selection.
  useEffect(() => {
    if (pageFirstRun.current) {
      pageFirstRun.current = false
      return
    }
    if (!selectionSpansAllPages) {
      ignoreNextSelectionEvent.current = true
      setSelectedIds([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const handleRestore = async (row: DossierCleanupRow) => {
    setActionInFlight(row.funnelId)
    try {
      await client.restoreDossierCleanup(row.funnelId)
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e) {
      console.error('Restore failed', e)
    } finally {
      setActionInFlight(null)
    }
  }

  const handleSoftDelete = async (row: DossierCleanupRow) => {
    setActionInFlight(row.funnelId)
    try {
      await client.softDeleteDossierCleanup(row.funnelId)
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e) {
      console.error('Soft delete failed', e)
    } finally {
      setActionInFlight(null)
    }
  }

  const handleUndoSoftDelete = async (row: DossierCleanupRow) => {
    setActionInFlight(row.funnelId)
    try {
      await client.undoSoftDeleteDossierCleanup(row.funnelId)
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e) {
      console.error('Undo soft-delete failed', e)
    } finally {
      setActionInFlight(null)
    }
  }

  const clearSelection = () => {
    ignoreNextSelectionEvent.current = true
    setSelectedIds([])
    setSelectionSpansAllPages(false)
  }

  const handleSelectAllMatching = async () => {
    if (selectingAll) return
    setSelectingAll(true)
    setLastJobMessage(null)
    try {
      const result = await client.listDossierCleanupIds({
        state: stateFilter,
        reason: reasonFilter === 'all' ? undefined : reasonFilter,
        search: debouncedSearch || undefined
      })
      ignoreNextSelectionEvent.current = true
      setSelectedIds(result.funnelIds)
      setSelectionSpansAllPages(true)
      if (result.truncated) {
        setLastJobMessage(
          `Te veel resultaten — alleen de eerste ${result.funnelIds.length} dossiers ` +
            `zijn geselecteerd. Werk je filter bij of voer de bulk-actie in stappen uit.`
        )
      }
    } catch (e: any) {
      console.error('Select all failed', e)
      setLastJobMessage(
        'Selecteren mislukt: ' +
          (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setSelectingAll(false)
    }
  }

  const handleBulkRestore = async () => {
    if (!selectedIds.length) return
    setBulkInFlight(true)
    setLastJobMessage(null)
    const requested = selectedIds.length
    try {
      const result = await client.bulkRestoreDossierCleanup(selectedIds)
      setLastJobMessage(
        `${result.data.affected} van ${requested} dossier(s) teruggezet.`
      )
      clearSelection()
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e: any) {
      console.error('Bulk restore failed', e)
      setLastJobMessage(
        'Bulk-terugzetten mislukt: ' +
          (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setBulkInFlight(false)
    }
  }

  const handleBulkSoftDelete = async () => {
    if (!selectedIds.length) return
    setBulkInFlight(true)
    setLastJobMessage(null)
    const requested = selectedIds.length
    try {
      const result = await client.bulkSoftDeleteDossierCleanup(selectedIds)
      setLastJobMessage(
        `${result.data.affected} van ${requested} dossier(s) verwijderd.`
      )
      clearSelection()
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e: any) {
      console.error('Bulk soft-delete failed', e)
      setLastJobMessage(
        'Bulk-verwijderen mislukt: ' +
          (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setBulkInFlight(false)
    }
  }

  const handleBulkUndoSoftDelete = async () => {
    if (!selectedIds.length) return
    setBulkInFlight(true)
    setLastJobMessage(null)
    const requested = selectedIds.length
    try {
      const result = await client.bulkUndoSoftDeleteDossierCleanup(selectedIds)
      setLastJobMessage(
        `${result.data.affected} van ${requested} dossier(s) teruggezet naar "Te beoordelen".`
      )
      clearSelection()
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e: any) {
      console.error('Bulk undo soft-delete failed', e)
      setLastJobMessage(
        'Terugzetten naar "Te beoordelen" mislukt: ' +
          (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setBulkInFlight(false)
    }
  }

  const handleResetAll = async () => {
    setResetInFlight(true)
    setLastJobMessage(null)
    try {
      const result = await client.resetAllDossierCleanup()
      setLastJobMessage(
        `Reset uitgevoerd: ${result.data.affected} markering(en) opgeheven.`
      )
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e: any) {
      console.error('Reset all failed', e)
      setLastJobMessage(
        'Reset mislukt: ' +
          (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setResetInFlight(false)
    }
  }

  const handleRunJob = async () => {
    setRunJobInFlight(true)
    setLastJobMessage(null)
    try {
      const result = await client.runDossierCleanup()
      setLastJobMessage(
        `Job uitgevoerd: ${result.data.patientCardsMarked} patiëntkaart(en) en ` +
          `${result.data.filesMarked} dossier(s) gemarkeerd.`
      )
      await Promise.all([fetchRows(), fetchSummary()])
    } catch (e: any) {
      console.error('Run job failed', e)
      setLastJobMessage(
        'Job mislukt: ' + (e?.response?.data?.message || e?.message || 'onbekende fout')
      )
    } finally {
      setRunJobInFlight(false)
    }
  }

  const columns: GridColDef<DossierCleanupRow>[] = useMemo(
    () => [
      {
        field: 'patient',
        headerName: 'Patiënt',
        // Columns with naturally variable content use `flex` + `minWidth`:
        // they fill leftover space on wide viewports, hit the floor on narrow
        // ones and let the grid horizontally scroll instead of truncating.
        flex: 1.4,
        minWidth: 200,
        sortable: false,
        renderCell: (params: GridCellParams<unknown, DossierCleanupRow>) => {
          const row = params.row
          // Use react-router-dom's Link via Material UI so SPA navigation works
          // (same flow as the caseload row click), while ctrl/cmd/middle-click
          // still opens a new tab via the native anchor behavior.
          return (
            <Link
              component={RouterLink}
              to={getRecordPath(row)}
              color="primary"
            >
              {formatPatient(row)}
            </Link>
          )
        }
      },
      {
        field: 'markedForDeletionReason',
        headerName: 'Type',
        // Chip column has a known-natural size; keep it fixed.
        width: 160,
        sortable: false,
        renderCell: (params: GridCellParams<unknown, DossierCleanupRow>) => (
          <Chip
            size="small"
            label={REASON_LABELS[params.row.markedForDeletionReason]}
            color={
              params.row.markedForDeletionReason === 'patient_card_60d'
                ? 'default'
                : 'secondary'
            }
          />
        )
      },
      {
        field: 'currentStatusName',
        headerName: 'Status',
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        valueGetter: (params) => params.row.currentStatusName || '—'
      },
      {
        field: 'practiceName',
        headerName: 'Praktijk',
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        valueGetter: (params) => params.row.practiceName || '—'
      },
      {
        field: 'primaryPsychologistName',
        headerName: 'Psycholoog',
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (params) => params.row.primaryPsychologistName || '—'
      },
      {
        field: 'deletionLastActivityAt',
        headerName: 'Laatste activiteit',
        // Formatted dates are fixed-width content; no need to flex.
        width: 160,
        sortable: false,
        valueGetter: (params) => formatDate(params.row.deletionLastActivityAt)
      },
      {
        field: 'markedForDeletionAt',
        headerName: 'Gemarkeerd op',
        width: 160,
        sortable: false,
        valueGetter: (params) => formatDate(params.row.markedForDeletionAt)
      },
      ...(stateFilter === 'soft_deleted'
        ? [
            {
              field: 'softDeletedAt',
              headerName: 'Verwijderd op',
              width: 160,
              sortable: false,
              valueGetter: (params: GridCellParams<unknown, DossierCleanupRow>) =>
                formatDate(params.row.softDeletedAt)
            } as GridColDef<DossierCleanupRow>,
            {
              field: 'softDeletedByEmail',
              headerName: 'Door',
              flex: 1,
              minWidth: 180,
              sortable: false,
              valueGetter: (params: GridCellParams<unknown, DossierCleanupRow>) =>
                params.row.softDeletedByEmail || '—'
            } as GridColDef<DossierCleanupRow>
          ]
        : []),
      {
        field: 'actions',
        headerName: 'Acties',
        // Both tabs show two icon buttons; reserve enough room for the
        // header text + icons to avoid the "A…" truncation.
        width: 110,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params: GridCellParams<unknown, DossierCleanupRow>) => {
          const row = params.row
          const isBusy = actionInFlight === row.funnelId
          const patientName = formatPatient(row)
          return (
            <Box display="flex" alignItems="center" gridGap={4}>
              <AlertDialog
                title={`${patientName} terugzetten?`}
                text={
                  stateFilter === 'soft_deleted'
                    ? 'Het dossier wordt teruggezet naar de actieve caseload. Zowel de soft-delete als de markering "te verwijderen" worden opgeheven.'
                    : 'De markering "te verwijderen" wordt opgeheven en het dossier verschijnt weer in de caseload.'
                }
                confirmText="Terugzetten"
                cancelText="Annuleren"
                onConfirm={() => handleRestore(row)}
                render={({ onClick }) => (
                  <Tooltip
                    title={
                      stateFilter === 'soft_deleted'
                        ? 'Volledig terugzetten naar caseload'
                        : 'Behouden (markering opheffen)'
                    }
                  >
                    {/* span needed for Tooltip to show when button is disabled */}
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={onClick}
                        disabled={isBusy}
                        aria-label={`Behouden ${patientName}`}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              />
              {stateFilter === 'marked' && (
                <AlertDialog
                  title={`${patientName} markeren als verwijderd?`}
                  text={
                    'Het dossier blijft (tijdens de droogloopperiode) bewaard in de database, ' +
                    'maar verdwijnt uit deze lijst en blijft onzichtbaar in de caseload. ' +
                    'Je kunt het later alsnog terugzetten via het tabblad "Verwijderd".'
                  }
                  confirmText="Verwijderen"
                  cancelText="Annuleren"
                  validationText="VERWIJDER"
                  onConfirm={() => handleSoftDelete(row)}
                  render={({ onClick }) => (
                    <Tooltip title="Verwijderen (soft-delete)">
                      <span>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={onClick}
                          disabled={isBusy}
                          aria-label={`Verwijderen ${patientName}`}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                />
              )}
              {stateFilter === 'soft_deleted' && (
                <AlertDialog
                  title={`${patientName} terugzetten naar "Te beoordelen"?`}
                  text={
                    'De soft-delete wordt opgeheven, maar het dossier blijft gemarkeerd ' +
                    'als "te verwijderen". Het verschijnt weer in het tabblad "Te beoordelen", ' +
                    'niet in de caseload.'
                  }
                  confirmText="Terug naar Te beoordelen"
                  cancelText="Annuleren"
                  onConfirm={() => handleUndoSoftDelete(row)}
                  render={({ onClick }) => (
                    <Tooltip title='Terug naar "Te beoordelen"'>
                      <span>
                        <IconButton
                          size="small"
                          onClick={onClick}
                          disabled={isBusy}
                          aria-label={`Terug naar Te beoordelen voor ${patientName}`}
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                />
              )}
            </Box>
          )
        }
      }
    ],
    [actionInFlight, stateFilter]
  )

  return (
    <Paper style={{ width: '100%' }}>
      <Box p={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5">Dossier-opschoning</Typography>
            <Typography variant="body2" color="textSecondary">
              Droogloop voor patiëntkaarten (60 d) en dossiers (90 d). Niets
              wordt automatisch verwijderd.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gridGap={8}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchRows()
                fetchSummary()
              }}
              disabled={loading}
            >
              Verversen
            </Button>
            <AlertDialog
              title="Markeer-job nu uitvoeren?"
              text="Hiermee wordt de droogloop direct uitgevoerd in plaats van te wachten op de dagelijkse cron. Bedoeld voor testen."
              confirmText="Uitvoeren"
              cancelText="Annuleren"
              onConfirm={handleRunJob}
              render={({ onClick }) => (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    runJobInFlight ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayArrowIcon />
                    )
                  }
                  onClick={onClick}
                  disabled={runJobInFlight}
                >
                  Job uitvoeren
                </Button>
              )}
            />
            <AlertDialog
              title="Alle markeringen resetten?"
              text={
                'Dit verwijdert ALLE markeringen en soft-deletes uit de database, op alle dossiers. ' +
                'Bedoeld als snelle ongedaan-maken-knop tijdens de droogloopfase. ' +
                'De actie raakt geen patiëntgegevens — alleen de droogloop-vlaggen.'
              }
              confirmText="Resetten"
              cancelText="Annuleren"
              validationText="RESET"
              onConfirm={handleResetAll}
              render={({ onClick }) => (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    resetInFlight ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <LayersClearIcon />
                    )
                  }
                  onClick={onClick}
                  disabled={resetInFlight}
                >
                  Reset alles
                </Button>
              )}
            />
          </Box>
        </Box>

        {lastJobMessage && (
          <Box mt={2}>
            <Typography variant="body2">{lastJobMessage}</Typography>
          </Box>
        )}

        {summary && (
          <Box mt={2} display="flex" gridGap={16}>
            <SummaryChip
              label="Patiëntkaarten gemarkeerd"
              value={summary.markedPatientCards}
            />
            <SummaryChip
              label="Dossiers gemarkeerd"
              value={summary.markedFiles}
            />
            <SummaryChip
              label="Soft-deleted"
              value={summary.softDeletedTotal}
            />
          </Box>
        )}

        <Box mt={3}>
          <Tabs
            value={stateFilter}
            onChange={(_e, v) => {
              setStateFilter(v as StateFilter)
              setPage(0)
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              label={`Te beoordelen${
                summary
                  ? ` (${summary.markedPatientCards + summary.markedFiles})`
                  : ''
              }`}
              value="marked"
            />
            <Tab
              label={`Verwijderd${
                summary ? ` (${summary.softDeletedTotal})` : ''
              }`}
              value="soft_deleted"
            />
          </Tabs>
        </Box>

        <Box
          mt={2}
          display="flex"
          alignItems="center"
          gridGap={12}
          flexWrap="wrap"
        >
          <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={reasonFilter}
              onChange={(e) => {
                setReasonFilter(e.target.value as ReasonFilter)
                setPage(0)
              }}
            >
              <MenuItem value="all">Alle</MenuItem>
              <MenuItem value="patient_card_60d">
                Patiëntkaart (60 d)
              </MenuItem>
              <MenuItem value="file_90d">Dossier (90 d)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Zoek op naam of e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 280 }}
          />
        </Box>

        {error && (
          <Box mt={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {selectedIds.length > 0 && (
          <Box
            mt={2}
            p={1.5}
            display="flex"
            alignItems="center"
            gridGap={12}
            flexWrap="wrap"
            style={{
              background: '#E3F2FD',
              border: '1px solid #BBDEFB',
              borderRadius: 4
            }}
          >
            <Box display="flex" alignItems="center" gridGap={8} style={{ flexGrow: 1 }}>
              <Typography variant="body2">
                <strong>{selectedIds.length}</strong> rij(en) geselecteerd
                {selectionSpansAllPages
                  ? ' (alle pagina\'s)'
                  : total > rows.length
                    ? ` (alleen deze pagina van ${total})`
                    : ''}
              </Typography>
              {/* Offer "select all matching" only when the entire visible page is
                  already selected and there's more data hiding on other pages. */}
              {!selectionSpansAllPages &&
                rows.length > 0 &&
                rows.every((r) => selectedIds.includes(r.funnelId)) &&
                total > rows.length && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleSelectAllMatching}
                    disabled={selectingAll}
                    style={{ cursor: selectingAll ? 'wait' : 'pointer' }}
                  >
                    {selectingAll
                      ? 'Bezig met selecteren…'
                      : `Selecteer alle ${total} resultaten`}
                  </Link>
                )}
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={clearSelection}
              disabled={bulkInFlight || selectingAll}
            >
              Selectie wissen
            </Button>
            <AlertDialog
              title={`${selectedIds.length} dossier(s) terugzetten?`}
              text="Voor alle geselecteerde dossiers wordt de markering en eventuele soft-delete opgeheven. Ze verschijnen direct weer in de caseload."
              confirmText="Terugzetten"
              cancelText="Annuleren"
              onConfirm={handleBulkRestore}
              render={({ onClick }) => (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={
                    bulkInFlight ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <RestoreIcon />
                    )
                  }
                  onClick={onClick}
                  disabled={bulkInFlight}
                >
                  Behouden ({selectedIds.length})
                </Button>
              )}
            />
            {stateFilter === 'marked' && (
              <AlertDialog
                title={`${selectedIds.length} dossier(s) verwijderen?`}
                text={
                  'Alle geselecteerde dossiers krijgen een soft-delete vlag en verdwijnen uit deze lijst. ' +
                  'Patiëntgegevens blijven bewaard; ze zijn later via het tabblad "Verwijderd" terug te zetten.'
                }
                confirmText="Verwijderen"
                cancelText="Annuleren"
                validationText="VERWIJDER"
                onConfirm={handleBulkSoftDelete}
                render={({ onClick }) => (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={
                      bulkInFlight ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <DeleteForeverIcon />
                      )
                    }
                    onClick={onClick}
                    disabled={bulkInFlight}
                  >
                    Verwijderen ({selectedIds.length})
                  </Button>
                )}
              />
            )}
            {stateFilter === 'soft_deleted' && (
              <AlertDialog
                title={`${selectedIds.length} dossier(s) terug naar "Te beoordelen"?`}
                text={
                  'Voor alle geselecteerde dossiers wordt alleen de soft-delete opgeheven. ' +
                  'Ze blijven gemarkeerd als "te verwijderen" en verschijnen weer in het tabblad ' +
                  '"Te beoordelen", niet in de caseload.'
                }
                confirmText="Terug naar Te beoordelen"
                cancelText="Annuleren"
                onConfirm={handleBulkUndoSoftDelete}
                render={({ onClick }) => (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      bulkInFlight ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <UnarchiveIcon />
                      )
                    }
                    onClick={onClick}
                    disabled={bulkInFlight}
                  >
                    Te beoordelen ({selectedIds.length})
                  </Button>
                )}
              />
            )}
          </Box>
        )}

        {/* The DataGrid root has `flex: 1`, which only takes effect when its
            parent is a flex container. In a plain block wrapper the grid
            sizes itself by content and leaves a gap on the right. Making
            the wrapper a flex container is the MUI-recommended layout. */}
        <Box mt={2} display="flex" style={{ width: '100%' }}>
          <DataGrid<DossierCleanupRow>
            autoHeight
            columns={columns}
            rows={rows}
            getRowId={(row) => row.funnelId}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize)
              setPage(0)
            }}
            loading={loading}
            checkboxSelection
            selectionModel={selectedIds}
            onSelectionModelChange={(model) => {
              // Swallow events that are just MUI echoing back a programmatic
              // selectionModel change (set-all, clear, page-change auto-clear).
              if (ignoreNextSelectionEvent.current) {
                ignoreNextSelectionEvent.current = false
                return
              }
              const next = model.map((id) => Number(id))
              setSelectedIds(next)
              // If the user manually checks/unchecks anything while we're in
              // "all pages selected" mode, drop the cross-page flag so the
              // banner stops claiming that all pages are still selected.
              if (selectionSpansAllPages) {
                setSelectionSpansAllPages(false)
              }
            }}
            disableSelectionOnClick
            disableColumnMenu
          />
        </Box>
      </Box>
    </Paper>
  )
}

const SummaryChip = ({ label, value }: { label: string; value: number }) => (
  <Paper
    variant="outlined"
    style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column' }}
  >
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Paper>
)

const DossierCleanupPage = (): JSX.Element => (
  <AdminPage>
    <DossierCleanupContent />
  </AdminPage>
)

export default DossierCleanupPage
